/**
 * Геометрия и слои одной ячейки (§4.2, §4.3, §5.5).
 *
 * Здесь живёт всё, что одинаково у ячеек любого типа: фиксированная ширина,
 * закрепление `position: sticky`, курсор ячейки, слой черновика, конфликта и
 * чужой свежей правки. Приоритет слоёв ровно один (§4.3):
 * конфликт → мой черновик → чужая свежая правка. Два слоя не рисуются никогда.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import type { ChannelCode, Conflict } from '@shared/types.ts'
import { IconConflict, Tooltip } from '../../../../components/index.js'
import type { ColumnDef, ColumnGroupCode } from '../columns.js'
import type { TableRow } from '../rows.js'
import type { ResolvedCell, ValueContext } from '../values.js'
import './cells.css'

/** Какой слой нарисован поверх значения. */
export type CellLayer = 'none' | 'draft' | 'conflict' | 'fresh'

/** Состояние ячейки по П3: значение, задача, неприменимо, нет в канале. */
export type CellState = 'value' | 'empty' | 'na' | 'off-channel'

export interface CellBoxProps {
  column: ColumnDef
  width: number
  stickyLeft?: number | undefined
  stickyRight?: number | undefined
  /** Последняя закреплённая слева / первая закреплённая справа — тень скролла. */
  edgeLeft?: boolean
  edgeRight?: boolean
  layer: CellLayer
  state: CellState
  active: boolean
  editing: boolean
  focusable: boolean
  readOnly: boolean
  rowIndex: number
  colIndex: number
  label: string
  tooltip?: ReactNode
  /** Клик по неактивной ячейке — поставить курсор. */
  onActivate?: () => void
  /** Клик по активной ячейке и двойной клик — открыть редактор (§5.1). */
  onOpen?: () => void
  children: ReactNode
}

export function CellBox({
  column, width, stickyLeft, stickyRight, edgeLeft, edgeRight, layer, state,
  active, editing, focusable, readOnly, rowIndex, colIndex, label, tooltip,
  onActivate, onOpen, children,
}: CellBoxProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Фокус держим на активной ячейке, пока не открыт редактор: иначе
  // скринридер теряет позицию, а стрелки перестают приходить в сетку (§13).
  useEffect(() => {
    if (!active || editing) return
    const node = ref.current
    if (!node) return
    const current = document.activeElement
    if (current === node) return
    /* Фокус забираем, только если он уже в таблице (или потерян вовсе). Иначе
       при перерисовке строк — а она идёт на каждую букву в поиске — заново
       смонтированная активная ячейка выдёргивала фокус из поля поиска, и
       печатать дальше было нельзя. */
    const grid = node.closest('.table__grid')
    const inside = current !== null && current !== document.body && grid?.contains(current)
    if (current !== null && current !== document.body && !inside) return
    node.focus({ preventScroll: true })
  }, [active, editing])

  const classes = ['cell', `cell--${column.align}`]
  // Граница группы принадлежит ячейке: слой поверх таблицы при прокрутке
  // наезжал на закреплённые колонки.
  if (column.groupStart) classes.push('cell--group-start')
  if (state !== 'value') classes.push(`cell--${state}`)
  if (layer !== 'none') classes.push(`cell--${layer}`)
  if (active) classes.push('cell--active')
  if (editing) classes.push('cell--editing')
  if (readOnly && state !== 'na' && state !== 'off-channel') classes.push('cell--readonly')
  if (stickyLeft !== undefined) classes.push('cell--sticky-left')
  if (stickyRight !== undefined) classes.push('cell--sticky-right')
  if (edgeLeft) classes.push('cell--edge-left')
  if (edgeRight) classes.push('cell--edge-right')
  if (column.tnum) classes.push('t-num')

  const box = (
    <div
      ref={ref}
      role="gridcell"
      aria-colindex={colIndex + 1}
      aria-label={label}
      aria-readonly={readOnly || undefined}
      tabIndex={active && !editing ? 0 : -1}
      data-row={rowIndex}
      data-col={colIndex}
      className={classes.join(' ')}
      style={{
        width: `${width}px`,
        ...(stickyLeft !== undefined ? { left: `${stickyLeft}px` } : null),
        ...(stickyRight !== undefined ? { right: `${stickyRight}px` } : null),
      }}
      onMouseDown={(event) => {
        if (!focusable) return
        // Фокус ставим сами: нативный фокус на div сбил бы редактор внутри.
        if (event.button !== 0) return
        if (active && !editing && onOpen) { event.preventDefault(); onOpen(); return }
        if (!active) { event.preventDefault(); onActivate?.() }
      }}
      onDoubleClick={() => { if (focusable) onOpen?.() }}
    >
      {layer === 'conflict' ? (
        <span className="cell__conflict" aria-hidden="true"><IconConflict size={16} /></span>
      ) : null}
      {tooltip
        ? <Tooltip content={tooltip}><span className="cell__hit">{children}</span></Tooltip>
        : children}
    </div>
  )

  // Тултип вешаем ВНУТРЬ ячейки, а не вокруг неё: обёртка тултипа стала бы
  // flex-элементом строки и забрала бы у ячейки `position: sticky` —
  // закреплённая колонка перестала бы закрепляться.
  return box
}

/** Единственный слой ячейки по приоритету §4.3. */
export function cellLayer(cell: ResolvedCell | null, conflict: Conflict | null, fresh: boolean): CellLayer {
  if (conflict) return 'conflict'
  if (cell?.isDraft) return 'draft'
  if (fresh) return 'fresh'
  return 'none'
}

/** Действия строки, которые дёргают нередактируемые ячейки (§4.3, §4.6, §5.2). */
export interface CellActions {
  /** Кат и шеврон: раскрыть/свернуть категории бренда. */
  expandRow: () => void
  /** 💬: открыть карточку бренда на вкладке «Комментарии». */
  openComments: () => void
  /** Клик по серой обводке в Р·О: переключить канал, сохранив позицию. */
  switchChannel: (channel: ChannelCode) => void
  /** Клик по зачёркнутой букве в Р·О: «Добавить в Опт» — правка в черновик. */
  addToChannel: (channel: ChannelCode) => void
  /** Клик по индикатору свёрнутой группы разворачивает группу. */
  expandGroup: (group: ColumnGroupCode) => void
}

/** Общие пропсы содержимого ячейки: один тип на все типы колонок (§5.2). */
export interface CellProps {
  column: ColumnDef
  row: TableRow
  cell: ResolvedCell
  ctx: ValueContext
  active: boolean
  editing: boolean
  readOnly: boolean
  /** Символ из режима «как в Google Sheets» (§5.1). */
  seed: string | null
  /** Запись правки в черновик. */
  write: (value: unknown) => void
  /** Зафиксировать и шагнуть по строкам, снова открыв редактор (§5.3). */
  stepAfterCommit: (direction: 1 | -1) => void
  /** Tab в редакторе: зафиксировать и вправо без открытия (§5.3). */
  commitAndRight: () => void
  openEditor: () => void
  closeEditor: () => void
  actions: CellActions
  /** Сколько полей зоны ответственности пусто — для колонки «Пропущено». */
  missing: number
}
